import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../abstract/api.service';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';

declare global {
  interface Window {
    ZohoDeskAsapReady: (callback?: () => void) => void;
    ZohoDeskAsap__asyncalls: Array<() => void> | null;
    ZohoDeskAsapReadyStatus: boolean;
    ZohoDeskAsap: {
      invoke: (method: string, data?: any) => void;
    };
  }
}

/**
 * Service to load and manage the Zoho Desk ASAP widget
 */
@Injectable({
  providedIn: 'root'
})
export class ZohoAsapService extends ApiService {
  private loaded = false;

  constructor() {
    super('zoho');
  }

  /**
   * Initialize the Zoho ASAP widget if enabled in environment config
   */
  initialize(): void {
    if (!environment.zohoAsap?.enabled || this.loaded) {
      return;
    }

    const scriptUrl = environment.zohoAsap.scriptUrl;
    if (!scriptUrl) {
      console.warn('Zoho ASAP widget enabled but no scriptUrl configured');
      return;
    }

    this.loadWidget(scriptUrl);
    this.loaded = true;
  }

  /**
   * Authenticate the current user with the ASAP widget
   * Call this after the user logs in
   */
  async authenticateUser(): Promise<void> {
    if (!environment.zohoAsap?.enabled) {
      return;
    }

    // Skip JWT authentication on localhost - Zoho doesn't allow localhost as a valid domain
    // The widget will still work, just without user context
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('[ZohoAsap] Skipping JWT auth on localhost (not supported by Zoho)');
      return;
    }

    try {
      // Fetch JWT from Hub
      const token = await firstValueFrom(
        this.request('GET', { path: 'asap-token' }, data => (isRecord(data) ? nullableString(data['token']) : undefined))
      );
      if (token) {
        // Wait for ASAP widget to be ready, then authenticate
        window.ZohoDeskAsapReady(() => {
          window.ZohoDeskAsap?.invoke('login', { jwtToken: token });
        });
      }
    } catch (error) {
      console.error('[ZohoAsap] Failed to authenticate user:', error);
    }
  }

  /**
   * Log out the user from the ASAP widget
   * Call this when the user logs out of the application
   */
  logout(): void {
    if (!environment.zohoAsap?.enabled) {
      return;
    }

    window.ZohoDeskAsapReady(() => {
      window.ZohoDeskAsap?.invoke('logout');
    });
  }

  private loadWidget(scriptUrl: string): void {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'zohodeskasapscript';
    script.defer = true;
    script.src = scriptUrl;

    // Insert script before the first script tag
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    // Set up the async ready handler
    window.ZohoDeskAsapReady = (callback?: () => void) => {
      const asyncCalls = window.ZohoDeskAsap__asyncalls = window.ZohoDeskAsap__asyncalls || [];
      if (window.ZohoDeskAsapReadyStatus) {
        if (callback) asyncCalls.push(callback);
        asyncCalls.forEach(fn => fn && fn());
        window.ZohoDeskAsap__asyncalls = null;
      } else if (callback) {
        asyncCalls.push(callback);
      }
    };
  }
}
